import React, { useEffect, useRef, useState } from 'react';
import { Events, TaskManagerApp } from 'make-traffic-integration-core';
import { ClaimModalOpenPayload } from 'make-traffic-integration-core/dist/types/events';
import ReactModal from 'react-modal';
import { useTheme } from 'styled-components';
import { centerModalStyles, ModalContainer } from 'app/components/common/common-styles';

export interface TaskClaimModalHostProps {
  taskManager: TaskManagerApp | null;
}

export const TaskClaimModalHost = ({ taskManager }: TaskClaimModalHostProps) => {
  const theme = useTheme();
  const modalStyle = {
    overlay: {
      backgroundColor: theme.styleguideColors.backgroundOverlay,
      zIndex: 50
    },
    content: {
      ...centerModalStyles,
      ...{
        border: 'none',
        backgroundColor: theme.styleguideColors.backgroundPrimary,
        borderTop: `4px solid ${theme.styleguideColors.contentRed}`,
        borderColor: theme.styleguideColors.contentRed,
        boxShadow: '0px 16px 48px rgba(26, 25, 25, 0.2)'
      }
    }
  };

  const [state, setState] = useState<ClaimModalOpenPayload | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<ClaimModalOpenPayload | null>(null);

  // keep latest state in a ref so callback ref can see it
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // subscribe once
  useEffect(() => {
    if (!taskManager) return;

    const handler = (payload: ClaimModalOpenPayload) => {
      setState(payload);
    };

    taskManager.subscribe(Events.ClaimModalOpen, handler);

    return () => {
      taskManager?.unsubscribe(Events.ClaimModalOpen, handler);
    };
  }, [taskManager]);

  // callback ref: called when div mounts
  const setContainerRef = (el: HTMLDivElement | null) => {
    containerRef.current = el;

    if (el && stateRef.current) {
      stateRef.current.provideHost({
        container: el,
        close: () => setState(null)
      });
    }
  };

  const isOpen = !!state;

  return (
    <ReactModal
      isOpen={isOpen}
      closeTimeoutMS={300}
      onRequestClose={() => {
        if (!state) return;
        setState(null);
        taskManager?.emit(Events.ClaimModalClosed, {
          task: state.task,
          reason: 'user_closed'
        });
      }}
      style={modalStyle}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <ModalContainer>
        <div ref={setContainerRef} />
      </ModalContainer>
    </ReactModal>
  );
};
